import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { ChevronLeft, ChevronRight, Plus, Loader2 } from "lucide-react";
import { workPlannerApi } from "@/services/workPlannerApi";
import { mpApi, serviceMeta, statusMeta, bucketOf, isOverdue, daysInMonth, dayKey, istToday, monthLabel, fmtDay } from "./mpShared";

const WEEKDAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

/** Month / Week / Day calendar of the month's deliverables with drag-to-reschedule. */
export default function CalendarTab({ month, businessId, filters, members, clients, openTask, newTask, refresh }) {
  const [tasks, setTasks] = useState(null);
  const [mode, setMode] = useState("month");
  const today = istToday();
  const [focus, setFocus] = useState(() => (today.slice(0, 7) === month ? today : dayKey(month, 1)));
  const clientName = useMemo(() => Object.fromEntries(clients.map((c) => [c.client_id, c.name])), [clients]);
  const memberName = useMemo(() => Object.fromEntries(members.map((m) => [m.user_id, m.name])), [members]);
  const touch = useRef(null);

  useEffect(() => { setFocus(today.slice(0, 7) === month ? today : dayKey(month, 1)); }, [month, today]);

  const load = useCallback(() => {
    const f = filters;
    return mpApi("/tasks", { params: {
      month, business_id: businessId || undefined, client_id: f.client_id, service: f.service, status: f.status,
      assigned_to: f.assigned_to, priority: f.priority, overdue: f.overdue ? "1" : "", search: f.search,
    } }).then((d) => setTasks(d.tasks)).catch((e) => { toast.error(e.message); setTasks([]); });
  }, [month, businessId, filters]);
  useEffect(() => { load(); }, [load]);

  const byDay = useMemo(() => {
    const m = {};
    for (const t of tasks || []) {
      const d = t.due_date ? String(t.due_date).slice(0, 10) : "";
      if (!d) continue;
      (m[d] = m[d] || []).push(t);
    }
    return m;
  }, [tasks]);
  const unscheduled = (tasks || []).filter((t) => !t.due_date);

  const moveTo = async (taskId, day) => {
    const t = (tasks || []).find((x) => (x.task_id || x._id) === taskId);
    if (!t || String(t.due_date || "").slice(0, 10) === day) return;
    setTasks((prev) => prev.map((x) => ((x.task_id || x._id) === taskId ? { ...x, due_date: day, start_date: day } : x)));
    try { await workPlannerApi.updateTask(taskId, { due_date: day, start_date: day }); toast.success(`Moved to ${fmtDay(day)}`); refresh(); }
    catch (e) { toast.error(e.message); load(); }
  };
  const dropProps = (day) => ({
    onDragOver: (e) => e.preventDefault(),
    onDrop: (e) => { e.preventDefault(); const id = e.dataTransfer.getData("text/task_id"); if (id) moveTo(id, day); },
  });

  // Period navigation inside the selected month.
  const shiftFocus = (days) => {
    const d = new Date(`${focus}T00:00:00`); d.setDate(d.getDate() + days);
    const k = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
    if (k.slice(0, 7) === month) setFocus(k);
  };
  const step = mode === "day" ? 1 : 7;
  const onTouchStart = (e) => { touch.current = e.touches[0].clientX; };
  const onTouchEnd = (e) => {
    if (touch.current === null || mode === "month") return;
    const dx = e.changedTouches[0].clientX - touch.current; touch.current = null;
    if (Math.abs(dx) > 60) shiftFocus(dx < 0 ? step : -step);
  };

  const Chip = ({ t, compact }) => {
    const m = serviceMeta(t.service); const over = isOverdue(t); const done = bucketOf(t.status) === "completed";
    return (
      <div draggable onDragStart={(e) => e.dataTransfer.setData("text/task_id", t.task_id || t._id)} onClick={(e) => { e.stopPropagation(); openTask(t); }}
        title={`${t.title} — ${clientName[t.client_id] || ""} · ${statusMeta(t.status).label}`}
        className={`cursor-grab rounded-md border-l-[3px] bg-white px-1.5 py-1 text-[11px] leading-tight shadow-sm ring-1 ring-slate-200 hover:ring-indigo-300 active:cursor-grabbing ${done ? "opacity-60" : ""} ${over ? "ring-rose-300" : ""}`}
        style={{ borderLeftColor: m.color }}>
        <div className={`truncate font-semibold text-slate-800 ${done ? "line-through" : ""}`}>{m.short} — {clientName[t.client_id] || "Client"}</div>
        {!compact && <div className="truncate text-slate-500">{t.title}</div>}
        {!compact && <div className="mt-0.5 flex items-center justify-between gap-1 text-[10px] text-slate-400">
          <span className="truncate">{(t.assigned_users || []).map((u) => memberName[u]).filter(Boolean)[0] || "Unassigned"}</span>
          <span className={over ? "font-semibold text-rose-600" : ""}>{over ? "Overdue" : statusMeta(t.status).label}</span>
        </div>}
      </div>
    );
  };

  // Month grid (Mon-first).
  const total = daysInMonth(month);
  const firstDow = (new Date(`${month}-01T00:00:00`).getDay() + 6) % 7;
  const cells = [...Array(firstDow).fill(null), ...Array.from({ length: total }, (_, i) => dayKey(month, i + 1))];
  while (cells.length % 7) cells.push(null);

  // Week containing focus (clipped to the month).
  const weekDays = useMemo(() => {
    const d = new Date(`${focus}T00:00:00`); const dow = (d.getDay() + 6) % 7; d.setDate(d.getDate() - dow);
    return Array.from({ length: 7 }, (_, i) => {
      const x = new Date(d); x.setDate(d.getDate() + i);
      return `${x.getFullYear()}-${String(x.getMonth() + 1).padStart(2, "0")}-${String(x.getDate()).padStart(2, "0")}`;
    });
  }, [focus]);

  return (
    <div className="space-y-3" onTouchStart={onTouchStart} onTouchEnd={onTouchEnd}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          {mode !== "month" && <button onClick={() => shiftFocus(-step)} className="rounded-lg border border-slate-200 bg-white p-1.5 text-slate-500 hover:bg-slate-50"><ChevronLeft className="h-4 w-4" /></button>}
          <span className="text-sm font-semibold text-slate-800">
            {mode === "month" ? monthLabel(month) : mode === "week" ? `${fmtDay(weekDays.find((d) => d.slice(0, 7) === month))} – ${fmtDay([...weekDays].reverse().find((d) => d.slice(0, 7) === month))}` : new Date(`${focus}T00:00:00`).toLocaleDateString("en-IN", { weekday: "long", day: "numeric", month: "long" })}
          </span>
          {mode !== "month" && <button onClick={() => shiftFocus(step)} className="rounded-lg border border-slate-200 bg-white p-1.5 text-slate-500 hover:bg-slate-50"><ChevronRight className="h-4 w-4" /></button>}
          {tasks === null && <Loader2 className="h-4 w-4 animate-spin text-slate-400" />}
          {tasks && <span className="text-xs text-slate-400">{tasks.length} task{tasks.length === 1 ? "" : "s"}</span>}
        </div>
        <div className="flex rounded-lg border border-slate-200 bg-white p-0.5">
          {["month", "week", "day"].map((m) => (
            <button key={m} onClick={() => setMode(m)} data-testid={`mp-cal-${m}`} className={`rounded-md px-3 py-1.5 text-xs font-medium capitalize ${mode === m ? "bg-slate-900 text-white" : "text-slate-600"}`}>{m}</button>
          ))}
        </div>
      </div>

      {mode === "month" && (
        <div className="overflow-x-auto rounded-2xl border border-slate-200 bg-white shadow-sm">
          <div className="grid min-w-[760px] grid-cols-7 border-b border-slate-100 bg-slate-50 text-center text-[11px] font-semibold uppercase tracking-wide text-slate-500">
            {WEEKDAYS.map((d) => <div key={d} className="py-2">{d}</div>)}
          </div>
          <div className="grid min-w-[760px] grid-cols-7">
            {cells.map((day, i) => {
              if (!day) return <div key={`e${i}`} className="min-h-[120px] border-b border-r border-slate-100 bg-slate-50/50" />;
              const list = byDay[day] || [];
              const isToday = day === today; const sunday = new Date(`${day}T00:00:00`).getDay() === 0;
              return (
                <div key={day} {...dropProps(day)} onClick={() => newTask({ due_date: day, start_date: day, client_id: filters.client_id || "", service: filters.service && filters.service !== "none" ? filters.service : "" })}
                  className={`group min-h-[120px] cursor-pointer border-b border-r border-slate-100 p-1.5 transition hover:bg-indigo-50/40 ${sunday ? "bg-slate-50/60" : ""}`}>
                  <div className="mb-1 flex items-center justify-between">
                    <button onClick={(e) => { e.stopPropagation(); setFocus(day); setMode("day"); }}
                      className={`flex h-6 w-6 items-center justify-center rounded-full text-xs font-semibold ${isToday ? "bg-indigo-600 text-white" : "text-slate-600 hover:bg-slate-200"}`}>{Number(day.slice(8))}</button>
                    <Plus className="h-3.5 w-3.5 text-slate-300 opacity-0 group-hover:opacity-100" />
                  </div>
                  <div className="space-y-1">
                    {list.slice(0, 3).map((t) => <Chip key={t._id} t={t} compact />)}
                    {list.length > 3 && <button onClick={(e) => { e.stopPropagation(); setFocus(day); setMode("day"); }} className="w-full text-left text-[11px] font-medium text-indigo-600">+{list.length - 3} more</button>}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {mode === "week" && (
        <div className="grid gap-2 md:grid-cols-7">
          {weekDays.map((day) => {
            const inMonth = day.slice(0, 7) === month; const list = byDay[day] || [];
            return (
              <div key={day} {...(inMonth ? dropProps(day) : {})} className={`min-h-[260px] rounded-xl border p-2 ${inMonth ? "border-slate-200 bg-white" : "border-dashed border-slate-200 bg-slate-50 opacity-50"}`}>
                <div className="mb-2 flex items-center justify-between">
                  <span className={`text-xs font-semibold ${day === today ? "text-indigo-600" : "text-slate-600"}`}>{new Date(`${day}T00:00:00`).toLocaleDateString("en-IN", { weekday: "short", day: "numeric" })}</span>
                  {inMonth && <button onClick={() => newTask({ due_date: day, start_date: day, client_id: filters.client_id || "" })} className="rounded p-0.5 text-slate-400 hover:bg-slate-100"><Plus className="h-3.5 w-3.5" /></button>}
                </div>
                <div className="space-y-1.5">{list.map((t) => <Chip key={t._id} t={t} />)}</div>
              </div>
            );
          })}
        </div>
      )}

      {mode === "day" && (
        <div {...dropProps(focus)} className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
          <div className="mb-3 flex items-center justify-between">
            <span className="text-sm text-slate-500">{(byDay[focus] || []).length} task(s)</span>
            <button onClick={() => newTask({ due_date: focus, start_date: focus, client_id: filters.client_id || "" })} className="inline-flex items-center gap-1 rounded-lg bg-indigo-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-indigo-700"><Plus className="h-3.5 w-3.5" /> Add task on this day</button>
          </div>
          {(byDay[focus] || []).length === 0 ? <div className="py-10 text-center text-sm text-slate-400">Nothing scheduled.</div>
            : <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">{(byDay[focus] || []).map((t) => <Chip key={t._id} t={t} />)}</div>}
        </div>
      )}

      {unscheduled.length > 0 && (
        <div className="rounded-xl border border-dashed border-slate-300 bg-white p-3">
          <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-400">No due date ({unscheduled.length}) — drag onto a day</div>
          <div className="flex flex-wrap gap-1.5">{unscheduled.map((t) => <div key={t._id} className="w-56"><Chip t={t} compact /></div>)}</div>
        </div>
      )}
      <p className="text-xs text-slate-400">Drag a task to another day to reschedule it. Click a day to create a task on it{mode !== "month" ? "; swipe left/right on mobile to change period" : ""}.</p>
    </div>
  );
}
